import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Rooted in Ongole",
    body:
      "We began in Ongole, Prakasam District — Andhra Pradesh, on the trade routes that have carried spice for centuries. Our work is quiet, hands-on, and slow by design.",
  },
  {
    n: "02",
    title: "Sourced, not bought",
    body:
      "We travel to the estates each season. Every lot of green cardamom is chosen for pod size, colour retention, moisture and volatile oil — never on price alone.",
  },
  {
    n: "03",
    title: "Traded, honestly",
    body:
      "We move fair, small-lot exports to importers who value origin. One number, one WhatsApp, one owner picking up. That's the whole operation — and we like it that way.",
  },
];

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1763558049571-a6d12a85472e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxncmVlbiUyMGNhcmRhbW9tJTIwcG9kc3xlbnwwfHx8fDE3ODU0OTM3NTJ8MA&ixlib=rb-4.1.0&q=85";

export const About = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-cream py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Section eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-4 mb-16 md:mb-24 font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/60"
        >
          <span className="text-gold">§</span>
          <span>About the House</span>
          <span className="flex-1 h-[1px] bg-charcoal/15 max-w-[220px]" />
          <span className="hidden md:inline">Manifesto</span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — headline */}
          <div className="lg:col-span-7">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
              className="font-serif font-light text-charcoal text-5xl md:text-6xl lg:text-[5.5rem] leading-[0.98] tracking-tight"
            >
              A trading house
              <br />
              <span className="italic text-forest">for the ones</span>
              <br />
              who still care
              <br />
              <span className="italic text-forest">where it grew.</span>
            </motion.h2>

            {/* Chapters */}
            <div className="mt-16 md:mt-20 divide-y divide-charcoal/10 border-t border-charcoal/10">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.8,
                    delay: i * 0.1,
                    ease: [0.65, 0, 0.35, 1],
                  }}
                  className="grid grid-cols-12 gap-6 py-8 md:py-10 group"
                  data-testid={`about-chapter-${c.n}`}
                >
                  <span className="col-span-2 md:col-span-1 font-serif text-2xl md:text-3xl text-gold italic">
                    {c.n}
                  </span>
                  <div className="col-span-10 md:col-span-11 grid md:grid-cols-12 gap-4 md:gap-8">
                    <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-charcoal group-hover:text-forest transition-colors">
                      {c.title}
                    </h3>
                    <p className="md:col-span-8 font-sans text-charcoal/70 text-base md:text-[17px] leading-relaxed max-w-[54ch]">
                      {c.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right — image + facts */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <div
              ref={imgRef}
              className="relative aspect-[3/4] w-full overflow-hidden rounded-sm bg-forest/10"
            >
              <motion.img
                style={{ y: imgY }}
                src={ABOUT_IMG}
                alt="Cardamom pods held in hand"
                className="h-[115%] w-full object-cover will-change-transform"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forestDeep/40 via-transparent to-transparent" />
              <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between text-cream">
                <div>
                  <div className="font-serif italic text-2xl leading-none">
                    Ongole
                  </div>
                  <div className="font-sans text-[11px] uppercase tracking-[0.3em] mt-2 text-cream/70">
                    Prakasam · AP
                  </div>
                </div>
                <div className="font-serif text-4xl italic text-gold leading-none">
                  15°30'N
                </div>
              </div>
            </div>

            {/* Small stat grid */}
            <div className="mt-8 grid grid-cols-3 gap-6 border-t border-charcoal/10 pt-8">
              {[
                { k: "8–10%", v: "Volatile oil" },
                { k: "7mm+", v: "Bold grade" },
                { k: "12+", v: "Export markets" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-3xl md:text-4xl text-forest">
                    {s.k}
                  </div>
                  <div className="mt-1 font-sans text-[11px] uppercase tracking-[0.24em] text-charcoal/55">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
